import _ from 'lodash'
import { SPARKY_TSG_MFE_PROPERTY_FALLBACK_EXTPOINT, type MfePropertyFallbackConfig } from '../extpoints'
import { prn } from '../framework/access'
import { indexArrayReadonly } from '../framework/utils'
import { BASE_ROLE, BROWSER_ROLE, FAWKES_APP_ID, MSP_BULK_CONFIG_APP_ID, PROP_FAWKES_VERSION, PROP_NETSEC_FEATURES, PROP_TSG_ACCESS_TOKEN_DECODED, PROP_TSG_INSTANCES } from '../framework/constants'
import type { ExtensionsConfig } from '../framework/microAppTypes'
import type { IamAccess, IndexedArray } from '../framework/stateTypes'

const NETSEC_PAI_ROLE_APP_IDS = ['prisma_access', 'prisma_access_panorama', 'all', '']
interface IFeature {
  mode: string
  feature: string
  description: string
  permission: string[]
  status: string
}

export default {
  point: SPARKY_TSG_MFE_PROPERTY_FALLBACK_EXTPOINT,
  configs: [
    {
      id: 'sparky.mfe.props.fallback.fawkes.version',
      config: {
        key: PROP_FAWKES_VERSION,
        source: 'fawkes',
        sync: true,
        generate({ properties, fxVars, setDynamicFallbackPropery }): string | undefined {
          const instances = properties.get(PROP_TSG_INSTANCES)
          if (!instances?.length) {
            return
          }
          const fawkesVersion = instances.get(FAWKES_APP_ID)?.runtime_attributes?.version
          if (fxVars.msp) {
            const mspVersion = instances.get(MSP_BULK_CONFIG_APP_ID)?.runtime_attributes?.version || fawkesVersion
            if (mspVersion) {
              setDynamicFallbackPropery({
                key: 'fawkes.bulk_config.version',
                value: mspVersion,
                source: 'fawkes',
              })
            }
          }
          return fawkesVersion
        }
      }
    },
    {
      id: 'sparky.mfe.props.fallback.netsec.features',
      config: {
        key: PROP_NETSEC_FEATURES,
        source: 'netsec',
        sync: true,
        generate({ properties, setDynamicFallbackPropery }): IndexedArray<IFeature> | undefined {
          const userRoles: string[] = []
          const getFeaturesFromProps = (key: string): IFeature[] => properties.get(key)?.data ?? []
          const access = properties.get(PROP_TSG_ACCESS_TOKEN_DECODED)?.access ?? {} as IamAccess
          // Get all the roles for the logged in user
          for (const key of Object.keys(access)) {
            const roleNames = access[key]
            const appId = prn.parse(key)?.app_id || ''
            const isPaiApp = NETSEC_PAI_ROLE_APP_IDS.includes(appId)
            if (isPaiApp) {
              userRoles.push(...roleNames)
            }
          }

          const getFeatures = (subtenant: string = '') => {
            const baseKey = `${PROP_NETSEC_FEATURES}.${subtenant}`.replace(/\.$/, '')
            // If the user has superuser access, then return the superuser features without any merge
            if (userRoles.includes('superuser')) {
              const key = `${baseKey}.superuser`
              const features = getFeaturesFromProps(key)
              if (!features.length) {
                return undefined // TODO: need to fallback for API if needed
              }
              return indexArrayReadonly(getFeaturesFromProps(key), 'feature')
            }

            const userFeatures = new Map<string, IFeature>()
            // Get all the features associated with the user roles with merged permissions
            for (const role of userRoles) {
              const key = `${baseKey}.${role}`
              const features = getFeaturesFromProps(key)

              // If any role is a custom role(missing MFE feature data)
              // return empty feature set to fallback to the feature API
              if (!features.length) {
                if ([BROWSER_ROLE, BASE_ROLE].includes(role)) {
                  continue // ignore those roles
                }
                return undefined // TODO: need to fallback for API if needed
              }

              try {
                features.forEach((feature) => {
                  const currentFeatureName = feature.feature
                  const currentFeature: IFeature = _.cloneDeep(feature)

                  // If feature is present from another role, merge the permissions
                  const existingUserFeatures = userFeatures.get(currentFeatureName)
                  if (existingUserFeatures) {
                    const existingPermissions = existingUserFeatures.permission

                    currentFeature.permission = _.uniq(existingPermissions.concat(feature.permission))
                  }

                  userFeatures.set(currentFeatureName, currentFeature)
                })
              }
              catch (e) {
                console.error('failed to extract netsec.features from MFE property, falling back to the feature API call', e)

                return undefined // TODO: need to fallback for API if needed
              }
            }

            return indexArrayReadonly(Array.from(userFeatures.values()), 'feature')
          }

          // Handle sub-tenants
          const subTenantIds: string[] = properties.get('netsec.subtenant')?.data?.map((t: any) =>
            t.sub_tenant_id as string
          )?.filter(Boolean)
          if (subTenantIds?.length) {
            for (const subtenant of subTenantIds) {
              const features = getFeatures(subtenant)
              if (features?.length) {
                setDynamicFallbackPropery({
                  key: `${PROP_NETSEC_FEATURES}.${subtenant}`,
                  value: features,
                  source: 'netsec',
                })
              }
            }
          }

          return getFeatures()
        }
      }
    }
  ]
} as ExtensionsConfig<MfePropertyFallbackConfig<any>>
